import { useMutation, useQuery, useQueryClient } from 'react-query';
import OneAuthAPI, { CrmContactInput, CrmResourceType } from 'services/API/OneAuthAPI';

export const CRM_PUSH_STATUS_QUERY_KEY = 'crm-push-status';

/** Whether the current user has pushed a resource to their CRM. */
export const useCrmPushStatus = (
  resourceType: CrmResourceType,
  resourceId?: string,
  enabled = true,
) => {
  return useQuery(
    [CRM_PUSH_STATUS_QUERY_KEY, resourceType, resourceId],
    async () => {
      const result = await OneAuthAPI.getCrmPushStatus(
        resourceType,
        resourceId as string,
      );

      return result?.data ?? { pushed: false };
    },
    { enabled: enabled && Boolean(resourceId) },
  );
};

/**
 * Pushes a resource into the user's connected CRM, then refreshes its push
 * status so the UI reflects the new/updated record.
 */
export const usePushToCrm = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      resourceType,
      resourceId,
      contact,
    }: {
      resourceType: CrmResourceType;
      resourceId: string;
      contact?: CrmContactInput;
    }) => OneAuthAPI.pushToCrm(resourceType, resourceId, contact),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          CRM_PUSH_STATUS_QUERY_KEY,
          variables.resourceType,
          variables.resourceId,
        ]);
      },
    },
  );
};
